import * as Yup from 'yup';
import { RegularExpressions } from '../../../../global/constants/regular-expressions';
import { getRequiredText } from '../../../../global/helpers/transformer';
import { ApiStatuses } from '../../../../global/models/api-model';
import { LabelSettingProps } from '../../../../global/models/epi-model';
import { Institution } from '../../../../global/models/institution-model';
import { InstitutionMailingAddressValidationSchema, InstitutionPhysicalAddressValidationSchema } from '../../../../global/validation-schemas/address-form-validation';
import { InstitutionPhoneValidationSchema } from '../../../../global/validation-schemas/phone-number-form-validation';
import { ListItem } from '../../../../search/search-model';

export interface ClergyDb {
	effective_date: string,
	assessment_percentage: number,
}

export interface LayDb extends ClergyDb {
	minimum_scheduled_hours: number,
}

export interface Dc extends ClergyDb {
	amendment_date: string,
	base_contribution: number,
	match_contribution: number,
	minimum_scheduled_hours: number,
	override_allowed: string,
}

export interface Rsvp {
	effective_date: string,
	amendment_date: string,
	base_contribution: number,
	base_contribution_type: string,
	match_contribution: number,
	match_contribution_type: string,
	minimum_scheduled_hours: number,
	contribution_type: string,
	eligible_population: string,

}
export interface PlanSummaryDto {
	id: number,
	name: string,
	effective_date: string,
	billing_schedule: string,
	status: string
	latest_amendment_date: string,
	employer_base: string,
	employer_match: string,
	minimum_scheduled_hours: string,
	allow_multiple_contribution_schedules: string,
	vesting_schedule: string,
	eligible_population: string,
	assessment_percent: string,
	termination_date: string,
	docusign_envelope_id: string,
}

export interface ActivedAndTerminatedPlan {
	active: PlanSummaryDto[],
	terminated: PlanSummaryDto[],
}

export interface PlanSummary {
	clergy_db: ActivedAndTerminatedPlan,
	lay_db: ActivedAndTerminatedPlan,
	dc: ActivedAndTerminatedPlan
	rsvp: ActivedAndTerminatedPlan,
}

export interface InstitutionItem extends ListItem {
	guid: string;
	client_number?: string,
	masked_client_number?: string;
	category?: string;
	legal_name?: string;
	report_name?: string;
	mailing_address?: any;
	city?: string;
	diocese?: string;
}

export interface DioceseInstitutionItem extends ListItem {
	organization_name: string;
	known_as: string,
	guid: string;
	client_number?: string,
	masked_client_number?: string;
	category?: string;
	city?: string;
	diocese?: string;
	type?: string;
	parish_ueid?: string;
	location_guid?: string;
	address1?: string;
	address2?: string;
	address3?: string;
	state?: string;
	postal_code?: string;
	country?: string;
	phone_country_code?: string;
	phone_area_code?: string;
	phone_number?: string;
	phone_extension?: string;
	email?: string;
	group_type?: string;
	group_role?: string;
	parent_id?: string;
	is_removed?: boolean;
	is_reconciled?: boolean;
	view_category?: string;
	view_id?: string;
	sort_column_value?: string;
	officers?: any[];
	parent_officers?: any[];
}
export interface OePlanTier {
	tier: string;
	rate: number;
}

export interface GhlPlans {
	medical: GhlPlan[];
	dental: GhlPlan[];
	std: GhlPlan[];
	ltd: GhlPlan[];
	group_life: GhlPlan[];
	uei: GhlPlan[];
}

export interface GhlPlan {
	plan_name: string;
	plan_code: string;
	billing_account_owner: string;
	billing_account_number: string;
	billing_account_name: string;
}

export interface OePlanOffer {
	group_id: string;
	group_name: string;
	plan_code: string;
	pbpor_id: string;
	plan_name: string;
	low_age: number;
	high_age: number;
	optout: boolean;
	pdf: string;
	tiers: OePlanTier[];
}

export interface OePlanOfferings {
	medical: OePlanOffer[];
	dental: OePlanOffer[];
}

export interface InstitutionState {
	institutionClientNumber: string,
	currentInstitution: Institution,
	patchInstitutionStatus: ApiStatuses,
	institutionSurveyFormStatus: ApiStatuses,
	addRequestStatus: ApiStatuses,
	removeRequestStatus: ApiStatuses,
	planSummary: PlanSummary,
	planSummaryLoaded: boolean,
	employeeListLoaded: boolean;
	institutionList: InstitutionItem[],
	institutionListLoaded: boolean,
	dioceseList?: InstitutionItem[],
	dioceseListLoaded?: boolean,
	institutionGuid?: string,
	dioceseInstitutionList?: DioceseInstitutionItem[],
	dioceseInstitutionListLoaded?: boolean,
	dioceseInstitutionClientNumber?: string,
	dioceseInstitutionGuid?: string,
	currentDioceseInstitution: DioceseInstitutionItem,
	manageInstitutionList?: InstitutionItem[],
	manageInstitutionListLoaded?: boolean,
	ghlPlan?: GhlPlans,
	ghlPlanLoaded?: boolean,
	oePlanOffering?: OePlanOfferings,
	oePlanOfferingLoaded?: boolean,
}


export enum InstitutionStateActions {
	SET_INSTITUTION_CLIENT_NUMBER = 'SET_INSTITUTION_CLIENT_NUMBER',
	SET_PLAN_SUMMARY = 'SET_PLAN_SUMMARY',
	UNLOAD_STATE = 'UNLOAD_STATE',
	SET_INSTITUTION_INFO = 'SET_INSTITUTION_INFO',
	PATCH_INSTITUTION_STATUS = 'PATCH_INSTITUTION_STATUS',
	SET_EMPLOYEELIST_LOADED = 'SET_EMPLOYEELIST_LOADED',
	SET_EMPLOYEELIST_UNLOADED = 'SET_EMPLOYEELIST_UNLOADED',
	SET_INSTITUTION_LIST = 'SET_INSTITUTION_LIST',
	SET_MANAGE_INSTITUTION_LIST = 'SET_MANAGE_INSTITUTION_LIST',
	INSTITUTION_SURVEY_STATUS = 'INSTITUTION_SURVEY_STATUS',
	SET_INSTITUTION_GUID = 'SET_INSTITUTION_GUID',
	SET_DIOCESE_LIST = 'SET_DIOCESE_LIST',
	SET_INSTITUTION_ADD_REQUEST_STATUS = 'SET_INSTITUTION_ADD_REQUEST_STATUS',
	SET_INSTITUTION_REMOVE_REQUEST_STATUS = 'SET_INSTITUTION_REMOVE_REQUEST_STATUS',
	SET_DIOCESE_INSTITUTION_LIST = 'SET_DIOCESE_INSTITUTION_LIST',
	SET_DIOCESE_INSTITUTION_CLIENT_NUMBER = 'SET_DIOCESE_INSTITUTION_CLIENT_NUMBER',
	SET_DIOCESE_INSTITUTION_GUID = 'SET_DIOCESE_INSTITUTION_GUID',
	SET_DIOCESE_INSTITUTION_INFO = 'SET_DIOCESE_INSTITUTION_INFO',
	SET_GHL_PLANS = 'SET_GHL_PLANS',
	SET_OE_PLAN_OFFERINGS = 'SET_OE_PLAN_OFFERINGS',
}

export function GetKnownAsValidationSchema(labelSettings: LabelSettingProps) {

	const validationSchema = Yup.string().required(getRequiredText(labelSettings?.requiredFormat, labelSettings?.institutionName))
		.max(240, labelSettings.maxCharactersError?.replace('{max}', '240') || 'Maximum 240 characters');

	return validationSchema;
}

export function GetBusinessEmailAddressValidationSchema(labelSettings: LabelSettingProps, isRequired: boolean = false) {

	let validationSchema = Yup.string();

	if (isRequired) {
		validationSchema = validationSchema.required(getRequiredText(labelSettings?.requiredFormat, labelSettings?.institutionEmailAddress));
	}

	validationSchema = validationSchema
		.email(labelSettings.emailFormatError)
		.max(320, labelSettings.maxCharactersError?.replace('{max}', '320') || 'Maximum 320 characters');

	return validationSchema;
}

export function GetWebsiteUrlValidationSchema(labelSettings: LabelSettingProps) {

	const validationSchema = Yup.string().matches(RegularExpressions.Website, `${labelSettings.invalidError || 'Invalid'} ${labelSettings.website}`).max(255, labelSettings.maxCharactersError?.replace('{max}', '255') || 'Maximum 255 characters');

	return validationSchema;
}

export function GetInstitutionDetailValidationSchema(labelSettings: LabelSettingProps) {

	return Yup.lazy(({
		business_phone_formatted,
		business_phone_country_code,
		fax_phone_formatted,
		fax_phone_country_code,
		org_type }) => {
		const isEmailRequired = org_type !== 'Trusts and Estates';
		const validationSchema = Yup.object().shape({
			known_as: GetKnownAsValidationSchema(labelSettings),
			...InstitutionMailingAddressValidationSchema(labelSettings),
			...InstitutionPhysicalAddressValidationSchema(labelSettings),
			...InstitutionPhoneValidationSchema(labelSettings,
				business_phone_formatted,
				business_phone_country_code,
				fax_phone_formatted,
				fax_phone_country_code),
			business_email_address: GetBusinessEmailAddressValidationSchema(labelSettings, isEmailRequired),
			website_url: GetWebsiteUrlValidationSchema(labelSettings),
		});

		return validationSchema;
	});
}


